'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';

import { toggleFavorite } from '@app/pdp/actions';
import { useProductDetails } from '@app/pdp/hooks/useProductDetails';
import { type MappedProductVariant } from '@libs/algolia';
import {
    joinOriginUrl,
    useIsExperimentFullSolution,
    usePageLayout,
    usePrevious,
    type Wishlist,
    type WishlistItem,
} from '@libs/common-next';
import { getWishList } from '@libs/ocapi/server';
import debounce from 'lodash.debounce';
import { useRouter } from 'next/navigation';
import { generateSfccCreateAccountUrl } from './generateCreateAccountUrl';
import { CommonFavoriteProductIcon } from './CommonFavoriteProductIcon';

interface SfccFavoriteProductIconProps {
    callAnalytics: (productId: MappedProductVariant) => void;
}

export default function SfccFavoriteProductIcon({
    callAnalytics,
}: SfccFavoriteProductIconProps) {
    const { selectedProduct, selectedVariants } = useProductDetails();
    const { ocapi } = usePageLayout();
    const isFullSolution = useIsExperimentFullSolution();

    const [wishList, setWishList] = useState<Wishlist>({});
    const { replace } = useRouter();

    const favoriteItem = useMemo(
        () =>
            wishList?.items?.find(
                (item: WishlistItem) =>
                    item.product_id === `${selectedProduct.id}`
            ),
        [selectedProduct.id, wishList?.items]
    );

    const [isFavorite, setIsFavorite] = useState(
        favoriteItem?.product_id === String(selectedProduct.id)
    );

    const prevFavoriteItem = usePrevious(favoriteItem);
    const prevProduct = usePrevious(selectedProduct);
    const prevIsFavorite = usePrevious(isFavorite);
    const isGuest = useMemo(
        () => ocapi.data?.session.customer?.auth_type !== 'registered',
        [ocapi]
    );

    const shouldPreventToggle =
        prevIsFavorite === undefined ||
        prevIsFavorite === isFavorite ||
        !!favoriteItem === isFavorite ||
        prevProduct?.id !== selectedProduct.id ||
        isGuest;

    const handleToggleFavorite = useCallback(() => {
        if (isGuest) {
            const path = generateSfccCreateAccountUrl(selectedProduct.id);
            replace(joinOriginUrl(path));
        } else {
            setIsFavorite(prev => !prev);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- Need to avoid unecessary renders
    }, [isGuest]);

    const callAddToFavorite = async (favoriteProduct: MappedProductVariant) => {
        setWishList(previousWishList => ({
            ...previousWishList,
            items: [
                ...(previousWishList.items ?? []),
                { product_id: favoriteProduct.id.toString() },
            ],
        }));
        await toggleFavorite({
            productId: favoriteProduct.id.toString(),
            listId: wishList?.id,
            isFavorite: true,
            isFullSolution,
        });
        callAnalytics(favoriteProduct);
    };

    const callRemoveFromFavorite = async (
        favoriteProduct: MappedProductVariant
    ) => {
        setWishList(previousWishList => ({
            ...previousWishList,
            items: (previousWishList.items ?? []).filter(
                item => item.product_id !== favoriteProduct.id.toString()
            ),
        }));
        await toggleFavorite({
            productId: favoriteItem?.id,
            listId: wishList?.id,
            isFavorite: false,
            isFullSolution,
        });
    };

    const customerId = ocapi.data?.session.customer?.customer_id ?? '0';
    useEffect(() => {
        const fetchData = async () => {
            const existingWishlists = await getWishList(
                customerId,
                isFullSolution
            );

            if (existingWishlists) {
                setWishList(existingWishlists);
            }
        };

        fetchData();
    }, [customerId, selectedVariants]);

    // eslint-disable-next-line react-hooks/exhaustive-deps -- Make sure debouce function is not recreating
    const debouncedCallToggleFavorite = useCallback(
        debounce(
            (isProductFavorite: boolean) =>
                isProductFavorite
                    ? callAddToFavorite(selectedProduct)
                    : callRemoveFromFavorite(selectedProduct),
            1000
        ),
        [selectedProduct.id, favoriteItem?.id]
    );

    useEffect(() => {
        if (
            (prevProduct?.id !== selectedProduct.id ||
                prevFavoriteItem?.product_id !== favoriteItem?.product_id) &&
            (favoriteItem?.product_id === String(selectedProduct.id)) !==
                isFavorite
        ) {
            setIsFavorite(
                favoriteItem?.product_id === String(selectedProduct.id)
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- Need to avoid unecessary renders
    }, [selectedProduct.id, favoriteItem?.product_id]);

    useEffect(() => {
        if (shouldPreventToggle) {
            return;
        }

        debouncedCallToggleFavorite(isFavorite);
    }, [isFavorite, debouncedCallToggleFavorite, shouldPreventToggle]);

    return (
        <CommonFavoriteProductIcon
            isFavorite={isFavorite}
            handleToggleFavorite={handleToggleFavorite}
            callAddToFavorite={callAddToFavorite}
            setIsFavorite={setIsFavorite}
        />
    );
}
